import { Calendar } from '@withjoy/joykit/icons';
import React from 'react';
import { ScheduleSection } from '../ScheduleSection';
import { SegmentedControl } from '../SegmentedControl/SegmentedControl';
import { FieldMetadata } from '@shared/components/ScheduleEditor/hooks/useScheduleFormState';
import { ScheduleEditorData } from '@shared/components/ScheduleEditor/types';
import { FormDatePicker } from '../FormDatePicker';
import { DateSelection, Flex } from '@withjoy/joykit';
import { FormTimeZonePicker } from '../FormTimeZonePicker';
import { useTranslation } from '@shared/core/i18n';

// Mapping of which components are visible for each selected index
const COMPONENT_VISIBILITY = {
  ['startDate']: [0, 1],
  ['endDate']: [1],
  ['timezone']: [0, 1],
};

const isVisible = (component: keyof typeof COMPONENT_VISIBILITY, selectedIndex: number) => {
  return COMPONENT_VISIBILITY[component].includes(selectedIndex);
};

const getDefaultSelectedIndex = (state: ScheduleEditorData): number => {
  if (!state.startTime) return 2; // TBA
  return state.endTime ? 1 : 0; // Start + End or Start Only
};

type TimeDateSectionProps = {
  state: ScheduleEditorData;
  setValue: (key: keyof ScheduleEditorData, value: string | DateSelection) => void;
  meta: Record<keyof ScheduleEditorData, FieldMetadata>;
};

export const TimeDateSection = (props: TimeDateSectionProps) => {
  const { state, setValue, meta } = props;

  // Save initial start and end times
  const initialStartTime = React.useRef(state.startTime);
  const initialEndTime = React.useRef(state.endTime);

  const [selectedIndex, setSelectedIndex] = React.useState(getDefaultSelectedIndex(state));

  const { t, tq } = useTranslation('scheduleEditor');
  const trans = t('form');

  return (
    <ScheduleSection
      icon={<Calendar size={24} />}
      title={tq`Date and Time`}
    >
      <Flex
        gap="12px"
        flexDirection="column"
      >
        <SegmentedControl
          onSelect={(index, _content) => {
            setSelectedIndex(index);

            // Restore or clear startTime
            if (!isVisible('startDate', index)) {
              setValue('startTime', null);
            } else {
              setValue('startTime', initialStartTime.current ?? new Date());
            }

            // Restore or clear endTime
            if (!isVisible('endDate', index)) {
              setValue('endTime', null);
            } else {
              const defaultEndTime = new Date();
              defaultEndTime.setHours(defaultEndTime.getHours() + 3); // Add 3 hours to the current time
              setValue('endTime', initialEndTime.current ?? defaultEndTime);
            }
          }}
          selectedIndex={selectedIndex}
          segments={[
            {
              label: tq`Start Only`,
            },
            {
              label: tq`Start + End`,
            },
            {
              label: tq`TBA`,
            },
          ]}
        ></SegmentedControl>
        <Flex
          gap="24px"
          flexDirection="column"
        >
          {isVisible('startDate', selectedIndex) && (
            <FormDatePicker
              fieldName={'startTime'}
              state={state}
              setValue={setValue}
              label={trans.startDateTitle()}
              meta={meta}
            />
          )}
          {isVisible('endDate', selectedIndex) && (
            <FormDatePicker
              fieldName={'endTime'}
              state={state}
              setValue={setValue}
              label={trans.endDateTitle()}
              meta={meta}
            />
          )}
          {isVisible('timezone', selectedIndex) && (
            <FormTimeZonePicker
              fieldName={'timezone'}
              state={state}
              setValue={setValue}
              label={trans.timezoneFieldTitle()}
              placeholder={trans.timezoneFieldPlaceholder()}
              meta={meta}
            />
          )}
        </Flex>
      </Flex>
    </ScheduleSection>
  );
};
